import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItemAction } from 'menu/MenuItem';
import { showContextMenu } from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';

interface UseChatInputHandlersOptions {
	prompt: string;
	inputRef: React.RefObject<HTMLTextAreaElement>;
	canSubmit: boolean;
	onSubmit: () => void;
	onUpdatePrompt: (prompt: string) => void;
	// Called before default paste behavior. Return true to suppress default text insertion.
	onBeforePaste?: (text: string) => boolean;
}

interface UseChatInputHandlersResult {
	onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
	handlePaste: (text: string) => void;
	onContextMenu: (e: React.MouseEvent) => Promise<void>;
}

export function useChatInputHandlers({
	prompt,
	inputRef,
	canSubmit,
	onSubmit,
	onUpdatePrompt,
	onBeforePaste,
}: UseChatInputHandlersOptions): UseChatInputHandlersResult {
	const dispatch = useDispatch();
	const isMobile = useSelector((r: any) => selectIsMobile(r.mode));

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLElement>) => {
			// On desktop: Enter submits, Shift+Enter adds line break
			// On mobile: Enter adds line break, user taps button to submit
			if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
				e.preventDefault();
				if (canSubmit) {
					onSubmit();
				}
			}
		},
		[isMobile, canSubmit, onSubmit]
	);

	const handlePaste = useCallback(
		(text: string) => {
			if (onBeforePaste?.(text)) return;

			// Insert the pasted text at the current cursor position in the prompt
			const textarea = inputRef.current;
			if (!textarea) {
				// Fallback to appending if ref is not available
				const newPrompt = prompt + text;
				onUpdatePrompt(newPrompt);
				return;
			}

			const cursorPosition = textarea.selectionStart || prompt.length;
			const newPrompt = prompt.slice(0, cursorPosition) + text + prompt.slice(cursorPosition);
			const newCursorPosition = cursorPosition + text.length;

			onUpdatePrompt(newPrompt);

			// Focus the input and set cursor position after pasting
			setTimeout(() => {
				if (textarea) {
					textarea.focus();
					textarea.setSelectionRange(newCursorPosition, newCursorPosition);
				}
			}, 0);
		},
		[prompt, inputRef, onUpdatePrompt, onBeforePaste]
	);

	const onContextMenu = useCallback(
		async (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			if (e.target instanceof HTMLElement) {
				const paste = await MenuItemAction.createPasteText(handlePaste);
				const menuItems = [paste];
				dispatch(
					showContextMenu({
						point: { x: e.clientX + 5, y: e.clientY },
						menuItems,
					})
				);
			}
		},
		[handlePaste, dispatch]
	);

	return {
		onKeyDown,
		handlePaste,
		onContextMenu,
	};
}
